import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, getCurrentUser } from '../lib/supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
  };

  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        const currentUser = await getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error('Error getting current user:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log(`Auth state changed: ${event}`, session?.user?.id);
        
        if (event === 'SIGNED_IN' && session?.user) {
          try {
            // Set user immediately to unblock UI
            setUser(session.user);
            setLoading(false);
            
            // Then try to get extended user data in background
            const currentUser = await getCurrentUser();
            setUser(currentUser);
          } catch (error) {
            console.error('Error getting user after sign in:', error);
            // Keep the basic user data even if profile fetch fails
            setUser(session.user);
            setLoading(false);
          }
        } else if (event === 'SIGNED_OUT') {
          console.log('User signed out, clearing user state');
          setUser(null);
          setLoading(false);
        } else if (event === 'USER_UPDATED' && session?.user) {
          setUser(session.user);
          setLoading(false);
        }
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      console.log('Starting signOut process...');
      await supabase.auth.signOut();
      console.log('Supabase auth.signOut() completed');
      
      setUser(null);
      console.log('User state cleared');
      
      // Clear any user-related data from localStorage
      localStorage.removeItem('manivo_subscription_info');
      localStorage.removeItem('dana_orb_dismissed_today');
      localStorage.removeItem('dana_orb_last_seen');
      
      // Clear consent state so user can re-consent if they log back in
      localStorage.removeItem('manivo_terms_accepted');
      localStorage.removeItem('manivo_cookie_consent');
      console.log('localStorage cleared');
      
      // Force a full page reload to ensure all client-side state is cleared
      window.location.reload();
      
      console.log('SignOut completed successfully');
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    signOut,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};